import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  FiArchive, FiChevronRight, FiDownload, FiEye, FiFile, FiFileText, FiFilm,
  FiFolder, FiFolderPlus, FiGrid, FiImage, FiList, FiMoreHorizontal,
  FiMusic, FiPlus, FiSearch, FiTrash2, FiUploadCloud, FiX,
} from 'react-icons/fi';
import BASE_URL from '../../config/config';
import { cabecerasAuth } from '../../queries/cliente';
import FilePreviewModal from '../files/FilePreviewModal';
import '../../css/adhoc-drive.css';

const ANCHO_MENU = 150;

const iconoArchivo = (item) => {
  const mime = item.mime_type || '';
  if (mime.startsWith('image/')) return FiImage;
  if (mime.startsWith('video/')) return FiFilm;
  if (mime.startsWith('audio/')) return FiMusic;
  if (mime.includes('zip') || mime.includes('compressed')) return FiArchive;
  if (mime.includes('pdf') || mime.includes('document') || mime.startsWith('text/')) return FiFileText;
  return FiFile;
};

const formatoTamano = (bytes, locale) => {
  if (!bytes) return '0 KB';
  const unidades = ['B', 'KB', 'MB', 'GB'];
  const indice = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), unidades.length - 1);
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: indice ? 1 : 0 }).format(bytes / (1024 ** indice))} ${unidades[indice]}`;
};

const DriveExplorer = ({ propietario, canManage = false, compact = false }) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [name, setName] = useState('');
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [menu, setMenu] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const endpoint = `${BASE_URL}/api/drive/${encodeURIComponent(propietario)}`;

  const load = async () => {
    try {
      setLoading(true);
      const response = await axios.get(endpoint, cabecerasAuth());
      setItems(response.data.items || []);
    } catch (error) {
      setNotice({ type: 'error', text: t('drive.errorCargar') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!propietario) return;
    let active = true;
    setLoading(true);
    axios.get(`${BASE_URL}/api/drive/${encodeURIComponent(propietario)}`, cabecerasAuth())
      .then((response) => { if (active) setItems(response.data.items || []); })
      .catch(() => { if (active) setNotice({ type: 'error', text: t('drive.errorCargar') }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [propietario, t]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  // La selección solo tiene sentido dentro de la carpeta visible
  useEffect(() => { setSeleccion([]); }, [folderId]);

  const cerrarMenu = useCallback(() => setMenu(null), []);

  // El menú vive en un portal: hay que cerrarlo ante cualquier cambio de layout
  useEffect(() => {
    if (!menu) return undefined;
    const alPulsar = (event) => { if (!event.target.closest('.drive-menu-pop, .drive-item-menu > button')) cerrarMenu(); };
    const alTeclear = (event) => { if (event.key === 'Escape') cerrarMenu(); };
    window.addEventListener('mousedown', alPulsar);
    window.addEventListener('keydown', alTeclear);
    window.addEventListener('resize', cerrarMenu);
    window.addEventListener('scroll', cerrarMenu, true);
    return () => {
      window.removeEventListener('mousedown', alPulsar);
      window.removeEventListener('keydown', alTeclear);
      window.removeEventListener('resize', cerrarMenu);
      window.removeEventListener('scroll', cerrarMenu, true);
    };
  }, [menu, cerrarMenu]);

  const abrirMenu = (event, item) => {
    event.stopPropagation();
    if (menu?.item.id === item.id) return cerrarMenu();
    const rect = event.currentTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.right - ANCHO_MENU, window.innerWidth - ANCHO_MENU - 8));
    setMenu({ item, top: rect.bottom + 4, left });
  };

  const currentItems = useMemo(() => items
    .filter((item) => (item.parent_id || null) === folderId)
    .filter((item) => item.nombre.toLowerCase().includes(search.trim().toLowerCase())), [items, folderId, search]);

  const seleccionados = useMemo(
    () => currentItems.filter((item) => seleccion.includes(item.id)),
    [currentItems, seleccion],
  );
  const todoSeleccionado = currentItems.length > 0 && seleccionados.length === currentItems.length;

  const alternarSeleccion = (id) => setSeleccion((previous) => (
    previous.includes(id) ? previous.filter((entry) => entry !== id) : [...previous, id]
  ));

  const alternarTodo = () => setSeleccion(todoSeleccionado ? [] : currentItems.map((item) => item.id));

  const breadcrumbs = useMemo(() => {
    const result = [];
    let cursor = folderId ? items.find((item) => item.id === folderId) : null;
    while (cursor) {
      result.unshift(cursor);
      cursor = cursor.parent_id ? items.find((item) => item.id === cursor.parent_id) : null;
    }
    return result;
  }, [folderId, items]);

  const createFolder = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${endpoint}/carpetas`, { nombre: name.trim(), parentId: folderId }, cabecerasAuth());
      setDialog(null); setName(''); await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || t('drive.errorCrear') });
    }
  };

  const uploadFiles = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length || !canManage) return;
    const formData = new FormData();
    selected.forEach((file) => formData.append('archivos', file));
    if (folderId) formData.append('parentId', folderId);
    try {
      setUploading(true);
      await axios.post(`${endpoint}/archivos`, formData, { headers: { ...cabecerasAuth().headers, 'Content-Type': 'multipart/form-data' } });
      setNotice({ type: 'success', text: t('drive.subidaCompleta', { count: selected.length }) });
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || t('drive.errorSubir') });
    } finally {
      setUploading(false); setDragOver(false);
    }
  };

  const renameItem = async () => {
    if (!dialog?.item || !name.trim()) return;
    try {
      await axios.patch(`${endpoint}/items/${dialog.item.id}`, { nombre: name.trim() }, cabecerasAuth());
      setDialog(null); setName(''); await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || t('drive.errorModificar') });
    }
  };

  const pedirEliminar = (objetivos) => {
    const lista = objetivos.filter(Boolean);
    if (!lista.length || !canManage) return;
    cerrarMenu();
    setConfirmar(lista);
  };

  // Si se borra la carpeta abierta (o una antecesora), hay que subir a un padre vivo
  const carpetaDestinoTrasBorrar = (eliminadosIds) => {
    if (!folderId) return null;
    let cursor = items.find((item) => item.id === folderId);
    const cadena = [];
    while (cursor) {
      cadena.unshift(cursor);
      cursor = cursor.parent_id ? items.find((item) => item.id === cursor.parent_id) : null;
    }
    const cortado = cadena.findIndex((carpeta) => eliminadosIds.includes(carpeta.id));
    if (cortado < 0) return folderId;
    return cortado === 0 ? null : cadena[cortado - 1].id;
  };

  const confirmarEliminar = async () => {
    const objetivos = confirmar || [];
    const ids = objetivos.map((item) => item.id);
    try {
      setEliminando(true);
      if (ids.length === 1) {
        await axios.delete(`${endpoint}/items/${ids[0]}`, cabecerasAuth());
      } else {
        await axios.delete(`${endpoint}/items`, { ...cabecerasAuth(), data: { ids } });
      }
      setFolderId(carpetaDestinoTrasBorrar(ids));
      setSeleccion([]);
      setConfirmar(null);
      setNotice({ type: 'success', text: t('drive.eliminadoOk', { count: ids.length }) });
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || t('drive.errorEliminar') });
      setConfirmar(null);
    } finally {
      setEliminando(false);
    }
  };

  const moveItem = async (id, parentId) => {
    if (!id || !canManage) return;
    const item = items.find((entry) => entry.id === id);
    if (!item || item.parent_id === parentId || item.id === parentId) return;
    try {
      await axios.patch(`${endpoint}/items/${id}`, { parentId }, cabecerasAuth());
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || t('drive.errorMover') });
    }
  };

  const reorder = async (targetId) => {
    if (!draggedId || draggedId === targetId) return;
    const ordered = [...currentItems];
    const from = ordered.findIndex((item) => item.id === draggedId);
    const to = ordered.findIndex((item) => item.id === targetId);
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    setItems((previous) => previous.map((item) => {
      const index = ordered.findIndex((entry) => entry.id === item.id);
      return index < 0 ? item : { ...item, posicion: index };
    }));
    await axios.put(`${endpoint}/orden`, { ids: ordered.map((item) => item.id) }, cabecerasAuth());
  };

  const download = async (item) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/drive/archivo/${item.id}/descargar`, { ...cabecerasAuth(), responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = item.nombre; anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setNotice({ type: 'error', text: t('drive.errorDescargar') });
    }
  };

  const openPreview = async (item) => {
    try {
      setPreview({ item, loading: true });
      const response = await axios.get(`${BASE_URL}/api/drive/archivo/${item.id}/previsualizar`, { ...cabecerasAuth(), responseType: 'blob' });
      setPreview({ item, url: URL.createObjectURL(response.data), mime: item.mime_type || response.data.type });
    } catch (error) {
      setPreview(null);
      setNotice({ type: 'error', text: error.response?.status === 415 ? t('drive.sinVistaPrevia') : t('drive.errorPrevisualizar') });
    }
  };

  const closePreview = () => setPreview(null);

  const carpetasEnConfirmacion = (confirmar || []).some((item) => item.tipo === 'carpeta');

  return (
    <section className={`adhoc-drive ${compact ? 'is-compact' : ''}`}>
      {notice && <div className={`drive-notice ${notice.type}`}><span>{notice.text}</span><button onClick={() => setNotice(null)}><FiX /></button></div>}

      <div className="drive-shell">
        <div className={`drive-content ${dragOver ? 'is-dragging' : ''}`} onDragOver={(e) => { if (canManage && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true); } }} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }} onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); uploadFiles(e.dataTransfer.files); } }}>
          {dragOver && <div className="drive-drop-overlay"><FiUploadCloud /><strong>{t('drive.sueltaArchivos')}</strong><span>{t('drive.hasta50')}</span></div>}
          <div className="drive-toolbar">
            <nav className="drive-breadcrumb" aria-label={t('drive.ruta')}><button onClick={() => { setFolderId(null); setSearch(''); }} onDragOver={(e) => { if (canManage) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); moveItem(Number(e.dataTransfer.getData('text/plain')), null); }}>{compact ? t('drive.unidadDe', { usuario: propietario }) : t('drive.miUnidad')}</button>{breadcrumbs.map((folder) => <React.Fragment key={folder.id}><FiChevronRight /><button onClick={() => setFolderId(folder.id)}>{folder.nombre}</button></React.Fragment>)}</nav>
            <div className="drive-actions">
              <label className="drive-search"><FiSearch /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('drive.buscar')} /></label>
              <div className="drive-view-toggle"><button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} aria-label={t('drive.vistaCuadricula')}><FiGrid /></button><button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')} aria-label={t('drive.vistaLista')}><FiList /></button></div>
              {canManage && <><button className="drive-btn secondary" onClick={() => { setName(''); setDialog({ type: 'folder' }); }}><FiFolderPlus /> {t('drive.nuevaCarpeta')}</button><button className="drive-btn primary" disabled={uploading} onClick={() => inputRef.current?.click()}><FiPlus /> {uploading ? t('drive.subiendo') : t('drive.subir')}</button></>}
            </div>
          </div>

          {canManage && seleccionados.length > 0 && (
            <div className="drive-bulk-bar">
              <label className="drive-bulk-check">
                <input type="checkbox" checked={todoSeleccionado} onChange={alternarTodo} />
                <span>{t('drive.seleccionados', { count: seleccionados.length })}</span>
              </label>
              <div className="drive-bulk-actions">
                <button onClick={() => setSeleccion([])}>{t('drive.cancelarSeleccion')}</button>
                <button className="danger" onClick={() => pedirEliminar(seleccionados)}><FiTrash2 /> {t('drive.eliminar')}</button>
              </div>
            </div>
          )}

          {loading ? <div className="drive-loading"><i /><i /><i /><i /></div> : currentItems.length === 0 ? (
            <div className="drive-empty"><div><FiFolder /></div><h2>{search ? t('drive.sinResultados') : t('drive.carpetaVacia')}</h2><p>{canManage ? t('drive.vaciaStaff') : t('drive.vaciaUsuario')}</p>{canManage && <button className="drive-btn primary" onClick={() => inputRef.current?.click()}><FiUploadCloud /> {t('drive.subirArchivos')}</button>}</div>
          ) : (
            <div className={`drive-items ${view}`}>
              {currentItems.map((item) => {
                const Icon = item.tipo === 'carpeta' ? FiFolder : iconoArchivo(item);
                const marcado = seleccion.includes(item.id);
                return (
                  <article key={item.id} className={`drive-item ${item.tipo} ${marcado ? 'is-selected' : ''}`} draggable={canManage} onDragStart={(e) => { setDraggedId(item.id); e.dataTransfer.setData('text/plain', item.id); }} onDragOver={(e) => { if (canManage) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const source = Number(e.dataTransfer.getData('text/plain')); item.tipo === 'carpeta' && source !== item.id ? moveItem(source, item.id) : reorder(item.id); }} onDoubleClick={() => item.tipo === 'carpeta' ? setFolderId(item.id) : openPreview(item)}>
                    {canManage && (
                      <label className="drive-item-select" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={marcado} onChange={() => alternarSeleccion(item.id)} aria-label={t('drive.seleccionarElemento', { nombre: item.nombre })} />
                      </label>
                    )}
                    <button className="drive-item-main" onClick={() => { if (canManage && seleccionados.length > 0) return alternarSeleccion(item.id); return item.tipo === 'carpeta' ? setFolderId(item.id) : openPreview(item); }}><span className="drive-item-icon"><Icon /></span><span className="drive-item-copy"><strong title={item.nombre}>{item.nombre}</strong><small>{item.tipo === 'carpeta' ? t('drive.carpeta') : formatoTamano(item.tamano, i18n.language)}</small></span></button>
                    {(item.tipo === 'archivo' || canManage) && <div className="drive-item-menu"><button aria-label={t('drive.acciones')} aria-expanded={menu?.item.id === item.id} onClick={(e) => abrirMenu(e, item)}><FiMoreHorizontal /></button></div>}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {menu && createPortal(
        <div className="drive-menu-pop" style={{ top: menu.top, left: menu.left, width: ANCHO_MENU }} role="menu">
          {menu.item.tipo === 'archivo' && <button onClick={() => { cerrarMenu(); openPreview(menu.item); }}><FiEye /> {t('drive.previsualizar')}</button>}
          {menu.item.tipo === 'archivo' && <button onClick={() => { cerrarMenu(); download(menu.item); }}><FiDownload /> {t('drive.descargar')}</button>}
          {canManage && <button onClick={() => { cerrarMenu(); setName(menu.item.nombre); setDialog({ type: 'rename', item: menu.item }); }}><FiFileText /> {t('drive.renombrar')}</button>}
          {canManage && <button className="danger" onClick={() => pedirEliminar([menu.item])}><FiTrash2 /> {t('drive.eliminar')}</button>}
        </div>,
        document.body,
      )}

      <input ref={inputRef} type="file" multiple hidden onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
      <FilePreviewModal
        file={preview && { ...preview, name: preview.item.nombre }}
        onClose={closePreview}
        onDownload={() => download(preview.item)}
      />
      {dialog && <div className="drive-dialog-backdrop" onMouseDown={() => setDialog(null)}><div className="drive-dialog" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}><div className="drive-dialog-icon">{dialog.type === 'folder' ? <FiFolderPlus /> : <FiFileText />}</div><h2>{dialog.type === 'folder' ? t('drive.nuevaCarpeta') : t('drive.renombrarElemento')}</h2><p>{dialog.type === 'folder' ? t('drive.nombreCarpetaHint') : t('drive.nombreElementoHint')}</p><input autoFocus value={name} maxLength={255} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') dialog.type === 'folder' ? createFolder() : renameItem(); }} /><div className="drive-dialog-actions"><button onClick={() => setDialog(null)}>{t('comun.cancelar')}</button><button className="primary" onClick={dialog.type === 'folder' ? createFolder : renameItem}>{t('comun.guardar')}</button></div></div></div>}

      {confirmar && (
        <div className="drive-dialog-backdrop" onMouseDown={() => !eliminando && setConfirmar(null)}>
          <div className="drive-dialog" role="alertdialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="drive-dialog-icon danger"><FiTrash2 /></div>
            <h2>{t('drive.confirmarEliminarTitulo')}</h2>
            <p>
              {confirmar.length === 1
                ? t('drive.confirmarEliminar', { nombre: confirmar[0].nombre })
                : t('drive.confirmarEliminarVarios', { count: confirmar.length })}
            </p>
            {carpetasEnConfirmacion && <p className="drive-dialog-warning">{t('drive.avisoCarpetasRecursivo')}</p>}
            <p className="drive-dialog-warning">{t('drive.accionIrreversible')}</p>
            <div className="drive-dialog-actions">
              <button disabled={eliminando} onClick={() => setConfirmar(null)}>{t('comun.cancelar')}</button>
              <button className="danger" disabled={eliminando} onClick={confirmarEliminar}>{eliminando ? t('drive.eliminando') : t('drive.eliminar')}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DriveExplorer;
